
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';
import DownloadExcel from 'vue-json-excel/JsonExcel.vue';

// --- Models ---
import { DriverLeave } from '@/models/DriverLeave.model';

// --- Services ---
import AppService from '@/services/app';
import DriverLeaveService from '@/services/driverLeave';
import DriverService from '@/services/driver';

// --- Third Party ---
import dayjs from 'dayjs';

@Component({
  components: {
    DownloadExcel,
  },
})
export default class DriversLeaveListing extends Vue {
  // used by Filter Area
  driverFilter: string = '';
  search: string = '';

  startDatePicker: boolean = false;
  startDate: string = '';
  endDatePicker: boolean = false;
  endDate: string = '';
  searchClicked: boolean = false;

  // used by No Data Slot
  isLoading: boolean = true;

  driverLeaveItems: DriverLeave[] = [];

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  title: string = '';
  clickedItem: DriverLeave = {
    id: 0,
    driverId: 0,
    name: '',
    nickname: '',
    startDate: '',
    endDate: '',
    totalDays: 0,
  };

  leaveDuration: string = '';
  pickerDate: string = dayjs().format('YYYY-MM-DD');
  startLeaveDatePicker: boolean = false;
  endLeaveDatePicker: boolean = false;

  valid: boolean = false;
  clicked: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
  };

  // used by Confirm Action Dialog for Delete Item
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;

  // #region Functions to display list data on page load
    formatDateRange(startDate: string, endDate: string) {
      return dayjs(startDate).format('DD MMM YYYY') + ' - ' + dayjs(endDate).format('DD MMM YYYY');
    }

    headers() {
      return [
        {
          sortable: false,
          text: '',
          value: 'onLeave',
        },
        {
          text: 'Nickname',
          value: 'nickname',
          filter: (value: string) => {
            if (this.driverFilter && this.driverFilter.length > 0) {
              return (value === this.driverFilter);
            } else {
              return true;
            }
          },
        },
        {
          text: 'Driver',
          value: 'name',
        },
        {
          text: 'Duration',
          value: 'leaveDates',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    customFilter(column: string, search: string, item: DriverLeave) {
      let result: boolean = false;
      
      if (search === '') {
        result = true;
      } else {
        result = item.name.toLowerCase().includes(search.toLowerCase()) ||
          (this.formatDateRange(item.startDate, item.endDate) + ' (' + item.totalDays + 'd)').toLowerCase().includes(search.toLowerCase());
      }

      return result;
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      switch (sortBy[0]) {
        case 'nickname':
          if (sortDesc[0]) {
            items.sort((a: DriverLeave, b: DriverLeave) => a.nickname.toLowerCase().localeCompare(b.nickname.toLowerCase()));
          } else {
            items.sort((a: DriverLeave, b: DriverLeave) => b.nickname.toLowerCase().localeCompare(a.nickname.toLowerCase()));
          }
          break;

        case 'name':
          if (sortDesc[0]) {
            items.sort((a: DriverLeave, b: DriverLeave) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
          } else {
            items.sort((a: DriverLeave, b: DriverLeave) => b.name.toLowerCase().localeCompare(a.name.toLowerCase()));
          }
          break;

        case 'leaveDates':
          // {{ formatDateRange(item.startDate, item.endDate) + ' (' + item.totalDays + 'd)' }}
          if (sortDesc[0]) {
            items.sort((a: DriverLeave, b: DriverLeave) => {
              const aDate = dayjs(a.startDate).format('YYYY-MM-DD') + ' - ' + dayjs(a.endDate).format('YYYY-MM-DD');
              const bDate = dayjs(b.startDate).format('YYYY-MM-DD') + ' - ' + dayjs(b.endDate).format('YYYY-MM-DD');

              return aDate.localeCompare(bDate);
            });
          } else {
            items.sort((a: DriverLeave, b: DriverLeave) => {
              const aDate = dayjs(a.startDate).format('YYYY-MM-DD') + ' - ' + dayjs(a.endDate).format('YYYY-MM-DD');
              const bDate = dayjs(b.startDate).format('YYYY-MM-DD') + ' - ' + dayjs(b.endDate).format('YYYY-MM-DD');

              return bDate.localeCompare(aDate);
            });
          }
          break;
      }

      return items;
    }

    async beforeMount() {
      await DriverService.listActive(true);
      await this.listAll();
    }

    getToday() {
      return dayjs().format('YYYY-MM-DD');
    }

    async listAll() {
      try {
        this.isLoading = true;
        const response: string = await DriverLeaveService.listAll(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        } else {
          this.driverLeaveItems = this.$store.getters['driversLeave/driversLeave'];
        }

      } finally {
        this.isLoading = false;
      }
    }

    driverOnLeave(item: DriverLeave) {
      // is today during this leave?
      const today = this.getToday();
      return ((dayjs(item.startDate).isSame(today) || dayjs(item.startDate).isBefore(today)) && (dayjs(item.endDate).isSame(today) || dayjs(item.endDate).isAfter(today)));
    }

    async searchLeave() {
      try {
        this.searchClicked = true;

        if (!this.startDate && !this.endDate) {
          this.driverLeaveItems = this.$store.getters['driversLeave/driversLeave'];
        } else {
          const response = await DriverLeaveService.search({ params: {
            startDate: this.startDate,
            endDate: this.endDate,
          }});

          if (response && response.data) {
            if (response.data.result && response.data.result === 'false') {
              AppService.errorHandler(response.data.message);
            } else {
              this.driverLeaveItems = response.data;
            }
          } else {
            // response is undefined or has no data field - SHOULD NEVER HAPPEN!
            throw new Error('response: ' + JSON.stringify(response));
          }
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriversLeave.vue - searchLeave - 642 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.searchClicked = false;
      }
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 5},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 5},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Administrator', level: 1},
        { type: 'Operator', level: 5},
      ]);
    }
  // #endregion

   // #region Functions required for the CSV Download 
    getDataForCSV() {
      return this.driverLeaveItems.reduce((a: any, b: any) => {
        let newItem = b;
        newItem = Object.assign({}, b);

        // now we have to manually apply the filter
        if (this.driverFilter && this.driverFilter.length > 0) {
          return (b.nickname === this.driverFilter) ? a.concat(newItem) : a;
        } else {
          return a.concat(newItem);
        }
      }, []);
    }

    getFieldsForCSV() {
      return {
        'Nickname': 'nickname',
        'Name': 'name',
        'Start Date': 'startDate',
        'End Date': 'endDate',
        'Total Days': 'totalDays',
      };
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update
    async launchNewItemDialog() {
      this.clickedItem = {
        id: 0,
        driverId: 0,
        name: '',
        nickname: '',
        startDate: '',
        endDate: '',
        totalDays: 0,
      };

      this.valid = false;

      this.title = 'Create New Leave for Driver';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).reset();
      }

      this.showItemDialog = true;
    }

    launchUpdateItemDialog(item: DriverLeave) {
      this.clickedItem = Object.assign({}, item);

      this.title = 'Update Leave for ' + (this.clickedItem.nickname);

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }
    
    leaveDateChanged() {
      this.clickedItem.totalDays = dayjs(this.clickedItem.endDate).diff(this.clickedItem.startDate , 'day') +1;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await DriverLeaveService.create({
          driverId: this.clickedItem.driverId,
          startDate: this.clickedItem.startDate,
          endDate: this.clickedItem.endDate,
          totalDays: this.clickedItem.totalDays,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriversLeave.vue - doCreate - 741 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await DriverLeaveService.update({
          id: this.clickedItem.id,
          driverId: this.clickedItem.driverId,
          startDate: this.clickedItem.startDate,
          endDate: this.clickedItem.endDate,
          totalDays: this.clickedItem.totalDays,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriversLeave.vue - doUpdate - 779 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

  // #endregion

  // #region Functions required for the Confirm Action Dialog for Delete
    launchDeleteItemDialog(item: DriverLeave) {
      this.clickedItem = item;

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212 ">delete</span> leave for <span style="font-weight: 700">' 
        + item.nickname + ' (' + item.name + ')</span><br/>'
        + ' from <span style="font-weight: 700">'
        + this.formatDateRange(item.startDate, item.endDate) + ' (' + item.totalDays + 'd)</span>.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DELETE') {
        await this.doDelete();
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await DriverLeaveService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('DriversLeave.vue - doDelete - 1028 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion
}
